import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRightIcon, BarChart3Icon, BookOpenIcon, EyeIcon, GraduationCapIcon, LayoutGridIcon, TargetIcon, UsersIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useModal } from '../components/ModalContext';
import { ProductScreenFrame } from '../components/home/ProductScreenFrame';
import { productScreens } from '../data/productScreens';
const brandIllustration = 'https://cdn.magicpatterns.com/uploads/gY6t59RZsAn2r4jY3LWSnn/Nisa_Duo_5.png';
const useCases = [{
  label: 'HQIM implementation',
  description: 'Align coaching to your curriculum',
  Icon: BookOpenIcon,
  bg: 'bg-orange-50/70',
  iconColor: 'text-orange-500'
}, {
  label: 'Expand coaching capacity',
  description: 'Do more with your existing team',
  Icon: UsersIcon,
  bg: 'bg-purple-50/70',
  iconColor: 'text-purple-500'
}, {
  label: 'Visibility across classrooms',
  description: 'A clear view of instructional patterns',
  Icon: EyeIcon,
  bg: 'bg-cyan-50/70',
  iconColor: 'text-cyan-600'
}, {
  label: 'Real-time coaching data',
  description: "What's working while it's happening",
  Icon: BarChart3Icon,
  bg: 'bg-amber-50/70',
  iconColor: 'text-amber-600'
}];
const setupItems = [{
  Icon: LayoutGridIcon,
  text: 'Your instructional frameworks'
}, {
  Icon: GraduationCapIcon,
  text: 'Your curriculum and instructional materials'
}, {
  Icon: TargetIcon,
  text: 'The goals your schools already set'
}];
const leaderSignals = ['Where the same instructional gap keeps surfacing', 'Questions coaches keep fielding about the curriculum', 'Coaches who are carrying more than they can hold', 'Progress worth bringing to a board or partner conversation'];
const trustBoundaries = ['Private coach thinking stays private', 'Debriefs stay between the coach and the teacher', 'No activity counts, no leaderboards'];
const rolloutSteps = [{
  number: '01',
  title: 'Configure with your leads',
  copy: 'A short setup pass with your coaching leads. Nothing to migrate.'
}, {
  number: '02',
  title: 'Coaches get to work',
  copy: 'They prepare, capture, and follow up the way they already do.'
}, {
  number: '03',
  title: 'The picture fills in',
  copy: 'After a few cycles, themes accumulate and the program comes into view.'
}];
export const ForSchools = () => {
  const {
    openTalkToUs
  } = useModal();
  return <div className="flex min-h-screen flex-col bg-white" data-id="element-458" data-mp="2ftim4x6s6uzs">
      <main className="relative flex-grow overflow-hidden" data-id="element-459" data-mp="2fw4w13h59ml6">
        <div className="w-full border-b border-gray-100 bg-gray-50/60" data-id="element-460" data-mp="2brx3swbh9oi7">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-461" data-mp="2psq09mlcatny">
            <div className="mx-auto flex max-w-6xl items-center gap-3 py-3" data-id="element-462" data-mp="2pq3tvqa6qk7b">
              <Link to="/#for-schools" className="group inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-primary-700" data-id="element-463" data-mp="2ku6crhr3l9th">
                <ArrowLeftIcon className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" strokeWidth={1.75} aria-hidden="true" data-id="element-464" data-mp="qg5hf6urb4y5" />
                Overview
              </Link>
              <span className="text-gray-300" aria-hidden="true" data-id="element-465" data-mp="372213blbjy98">/</span>
              <span className="text-sm font-medium text-teal-950" data-id="element-466" data-mp="2sd5uzk2tovb9">Schools and districts</span>
            </div>
          </div>
        </div>

        <section className="w-full bg-white pb-14 pt-12 md:pb-20 md:pt-16" data-id="element-467" data-mp="2xao71jc4zqnh">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-468" data-mp="2jbofa2tbuvn3">
            <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-469" data-mp="2jekgc00h20fr">
              <div className="lg:col-span-6" data-id="element-470" data-mp="2o8y3xg8w739v">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-471" data-mp="29eth5l0yux9c">
                  For schools and districts
                </p>
                <h1 className="text-balance font-serif text-[34px] font-normal leading-[1.08] text-teal-950 sm:text-[42px] lg:text-[46px]" data-id="element-472" data-mp="7mng0oxo1i9e">
                  Read across every coaching thread in your schools
                </h1>
                <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-473" data-mp="28p65ugkx7zee">
                  Every coach keeps a thread with every teacher: visits, goals,
                  evidence, follow-up, in the order they happened. Nisa reads
                  across them so the program comes into view.
                </p>
                <button type="button" onClick={openTalkToUs} className="mt-8 inline-flex min-h-12 items-center justify-center rounded-xl bg-primary-700 px-7 py-3 font-medium text-white shadow-sm transition-colors hover:bg-primary-800 focus-visible:ring-primary-600" data-id="element-474" data-mp="1uovdta7vkern">
                  Request a demo
                </button>
                <p className="mt-6 max-w-md text-sm leading-relaxed text-gray-500" data-id="element-475" data-mp="1bym65ktbjusk">
                  Working statewide with the Rhode Island Department of
                  Education, and with coaching teams at NYC Public Schools.
                </p>
              </div>

              <div className="lg:col-span-6" data-id="element-476" data-mp="1217xw8hjucbc">
                <div className="relative mx-auto max-w-lg" data-id="element-477" data-mp="rx12yoa9cc6d">
                  <div aria-hidden="true" className="pointer-events-none absolute inset-6 rounded-[46%_54%_58%_42%/56%_44%_56%_44%] bg-gradient-to-br from-cyan-100/70 via-primary-50 to-amber-100/50 blur-2xl" data-id="element-478" data-mp="rylvk8d0hl1l" />
                  <img src={brandIllustration} alt="Illustration of a coach and teacher in conversation among layered cards" className="relative w-full object-contain" data-id="element-479" data-mp="11sujeir3kptf" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-white pb-section-mobile md:pb-section-desktop" data-id="element-480" data-mp="e5w6fv26s009">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-481" data-mp="j36581bgto0r">
            <div className="mx-auto grid max-w-6xl gap-10 border-t border-gray-200 pt-12 lg:grid-cols-12 lg:gap-16 md:pt-16" data-id="element-482" data-mp="nuxqpxfrl3k1">
              <div className="lg:col-span-5" data-id="element-483" data-mp="3mlppagjzpnkh">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-484" data-mp="3mitq6oweosug">
                  The challenge
                </p>
                <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-teal-950 md:text-h2-desktop" data-id="element-485" data-mp="3ji9slh3i2x9s">
                  Why coaching is hard to see at scale
                </h2>
              </div>
              <div className="lg:col-span-7" data-id="element-486" data-mp="3t8l69vczkktv">
                <p className="text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-487" data-mp="3oabfry27i8el">
                  Most leaders piece their coaching program together from
                  observation notes, check-ins, and end-of-cycle reports. You
                  can see what happened in one classroom. Whether three teachers
                  hit the same wall last month, or how the curriculum is landing
                  in the rooms nobody visited, is harder to answer.
                </p>
                <p className="mt-5 border-l-2 border-primary-400 pl-4 font-serif text-xl leading-snug text-teal-900" data-id="element-488" data-mp="pse45ta7yvn7">
                  Leaders know their coaches are doing good work. Seeing it
                  whole is the harder part.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-primary-50/30 py-section-mobile md:py-section-desktop" data-id="element-489" data-mp="xc6xt9vulutg">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-490" data-mp="jcntyta2l5my">
            <div className="mx-auto max-w-6xl" data-id="element-491" data-mp="je8j23dm7wdf">
              <div className="max-w-3xl" data-id="element-492" data-mp="nczd21msaxwb">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-493" data-mp="nftudkai42li">
                  How it works
                </p>
                <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-teal-950 md:text-h2-desktop" data-id="element-494" data-mp="1g2ofmjf57601">
                  Built on what coaches already do
                </h2>
                <p className="mt-6 text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-495" data-mp="16bjxxby3171i">
                  Nisa follows the shape of coaching: preparing for a visit,
                  reflecting on instruction, following up with the teacher.
                  Those three habits build the picture. Coaches log nothing
                  extra.
                </p>
                <p className="mt-5 text-[15px] leading-relaxed text-gray-500" data-id="element-496" data-mp="15bjn5c492wee">
                  Evaluating what your coaches get day to day?{' '}
                  <Link to="/#features" className="group inline-flex items-center gap-1.5 font-medium text-primary-700 underline decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-800" data-id="element-497" data-mp="10d8hldkzn81z">
                    See the coach workspace
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={1.75} aria-hidden="true" data-id="element-498" data-mp="1o2axafw2aqtu" />
                  </Link>
                </p>
              </div>

              <div className="mt-11" data-id="element-499" data-mp="1e4n2jpyghjbo">
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500" data-id="element-500" data-mp="1e3lkh1j0vwkg">
                  Key use cases
                </h3>
                <div className="mt-5 grid gap-4 sm:grid-cols-2" data-id="element-501" data-mp="2of5u55ph27x">
                  {useCases.map(({
                  label,
                  description,
                  Icon,
                  bg,
                  iconColor
                }) => <div key={label} className={`flex items-start gap-4 rounded-xl border border-gray-100 ${bg} p-5`} data-id="element-502" data-mp="2jbo604aq4343">
                      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconColor}`} strokeWidth={1.75} aria-hidden="true" data-id="element-503" data-mp="2o84wv69b4vkv" />
                      <div data-id="element-504" data-mp="2y016nekraa4x">
                        <p className="font-medium text-teal-950" data-id="element-505" data-mp="2y0sq5r12wheu">{label}</p>
                        <p className="mt-1 text-sm leading-snug text-gray-600" data-id="element-506" data-mp="321cp9057pv3m">
                          {description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-gray-200/70 bg-gray-50 py-14 md:py-20" data-id="element-507" data-mp="1cmodn14g4h6n">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-508" data-mp="1cp109rl5ffq1">
            <div className="mx-auto max-w-6xl" data-id="element-509" data-mp="1hmkjz70m8kpd">
              <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-510" data-mp="1mobu799s87yj">
                <div className="lg:col-span-5" data-id="element-511" data-mp="1mqoh80ued8ig">
                  <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-512" data-mp="1rohnetvpa2iq">
                    What leaders see
                  </p>
                  <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-teal-950 md:text-h2-desktop" data-id="element-513" data-mp="20o4mh90yiw8m">
                    Useful visibility, with clear boundaries
                  </h2>
                  <p className="mt-5 text-[17px] leading-relaxed text-gray-600" data-id="element-514" data-mp="2alr4w5hgt5xc">
                    The view comes out of the coaching itself. No separate
                    reporting cycle, no forms for anyone to fill in at the end
                    of a quarter.
                  </p>

                  <div className="mt-8 grid gap-7 sm:grid-cols-2 lg:grid-cols-1 lg:gap-6" data-id="element-515" data-mp="2oe7fbrcp468a">
                    <div data-id="element-516" data-mp="2tcios0z2wepz">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-gray-500" data-id="element-517" data-mp="2tdts5zom3kgk">
                        What becomes clearer
                      </h3>
                      <ul className="mt-3 divide-y divide-gray-200" data-id="element-518" data-mp="cqlqaq3uxguu">
                        {leaderSignals.map(item => <li key={item} className="py-2.5 text-[15px] leading-snug text-gray-700" data-id="element-519" data-mp="8ogyzy0vswbd">
                            {item}
                          </li>)}
                      </ul>
                    </div>
                    <div data-id="element-520" data-mp="10g6b7ejkmo6r">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-gray-500" data-id="element-521" data-mp="10feqwzvfccvs">
                        What stays protected
                      </h3>
                      <ul className="mt-3 divide-y divide-gray-200" data-id="element-522" data-mp="rmta3pbcsg9l">
                        {trustBoundaries.map(item => <li key={item} className="py-2.5 text-[15px] leading-snug text-gray-700" data-id="element-523" data-mp="mpba4ofbdbdq">
                            {item}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7" data-id="element-524" data-mp="1y9fq4d8yyt9e">
                  <ProductScreenFrame assetId="LEADER_TODAY" title="Leader Hub · Ask Nisa and latest signals overview" src={productScreens.LEADER_TODAY.src} alt={productScreens.LEADER_TODAY.alt} aspectClassName="aspect-[4/3]" imageClassName="object-cover object-top" data-id="element-525" data-mp="1yaqtibyi5yzz" />
                  <p className="mt-3 text-sm leading-relaxed text-gray-500" data-id="element-526" data-mp="1ti7gbg3q95vp">
                    Themes drawn from coaching activity
                  </p>
                </div>
              </div>

              <p className="mt-10 max-w-3xl border-t border-gray-200 pt-6 text-sm leading-relaxed text-gray-500" data-id="element-527" data-mp="1khrhwl1ucoxp">
                The same division holds everywhere in Nisa. Coaches set
                direction, Nisa handles synthesis, leaders see themes.{' '}
                <Link to="/#proof" className="font-medium text-primary-700 underline decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-800" data-id="element-528" data-mp="1alxta3aul4zk">
                  How trust is built into the product
                </Link>
                {' · '}
                <Link to="/security" className="group inline-flex items-center gap-1.5 font-medium text-primary-700 underline decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-800" data-id="element-529" data-mp="tbc3qfpwo4kb">
                  Security and data practices
                  <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} aria-hidden="true" data-id="element-530" data-mp="y9vsenvvvugd" />
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-section-mobile md:py-section-desktop" data-id="element-531" data-mp="181jcu4u0wb8c">
          <div className="container mx-auto px-5 sm:px-8" data-id="element-532" data-mp="1lyoi42lwx62j">
            <div className="mx-auto max-w-6xl" data-id="element-533" data-mp="1gzli4wkshq1f">
              <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-534" data-mp="1h0wqzat3dnze">
                <div className="lg:col-span-7" data-id="element-535" data-mp="3nr353g3bd0pt">
                  <ProductScreenFrame assetId="A5" title="Support grounded in curriculum and coaching context" src={productScreens.A5.src} alt={productScreens.A5.alt} aspectClassName="aspect-[4/3] sm:aspect-[16/10]" imageClassName="object-contain" data-id="element-536" data-mp="3irqa282agnc5" />
                </div>
                <div className="lg:col-span-5" data-id="element-537" data-mp="3p9teajlh0e5t">
                  <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-538" data-mp="3u6jur816tbrr">
                    Your setup
                  </p>
                  <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-teal-950 md:text-h2-desktop" data-id="element-539" data-mp="2y5ymdg63rou">
                    Built around your schools
                  </h2>
                  <p className="mt-5 text-[17px] leading-relaxed text-gray-600" data-id="element-540" data-mp="36ewd7gptfd25">
                    Nisa gets tuned to your curriculum, your frameworks, your
                    calendar, your priorities. Coaches open it and recognize the
                    vocabulary.
                  </p>
                  <ul className="mt-7 space-y-3.5" data-id="element-541" data-mp="2iptt827pl84h">
                    {setupItems.map(({
                    Icon,
                    text
                  }) => <li key={text} className="flex items-start gap-3 text-[16px] leading-relaxed text-gray-700" data-id="element-542" data-mp="2g66xgjiy0ror">
                        <Icon className="mt-1 h-[18px] w-[18px] shrink-0 text-primary-600" strokeWidth={1.75} aria-hidden="true" data-id="element-543" data-mp="2b9gftrrhpo18" />
                        {text}
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="mt-14 border-t border-gray-200 pt-10 md:mt-16" data-id="element-544" data-mp="1x843dqbjg4k3">
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500" data-id="element-545" data-mp="1xa73j36ene2j">
                  How it starts
                </h3>
                <div className="mt-5 divide-y divide-gray-100 border-y border-gray-100" data-id="element-546" data-mp="1njm8fppxwrod">
                  {rolloutSteps.map(step => <div key={step.number} className="grid gap-3 py-6 sm:grid-cols-12 sm:gap-6" data-id="element-547" data-mp="1iksvbyekf2oq">
                      <p className="text-sm font-semibold text-primary-600 sm:col-span-1" data-id="element-548" data-mp="1dj3icd7s8ey0">{step.number}</p>
                      <h4 className="font-serif text-xl text-teal-950 sm:col-span-4" data-id="element-549" data-mp="19jbeuex8wyzi">{step.title}</h4>
                      <p className="leading-relaxed text-gray-600 sm:col-span-7" data-id="element-550" data-mp="yw4tmq0b7ymz">{step.copy}</p>
                    </div>)}
                </div>
              </div>

              <div className="relative mt-12 overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 via-white to-cyan-50/70 px-6 py-9 sm:px-10 md:mt-14 md:py-11" data-id="element-551" data-mp="pxrpwsaitz01">
                <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" data-id="element-552" data-mp="1ilnhjil8h0py" />

                <div className="relative max-w-2xl" data-id="element-553" data-mp="1mlz3cypc1730">
                  <h3 className="text-balance font-serif text-2xl font-normal leading-snug text-teal-950 md:text-3xl" data-id="element-554" data-mp="18vvpav0lwjon">
                    One school or the whole district
                  </h3>
                  <p className="mt-4 text-[17px] leading-relaxed text-gray-600" data-id="element-555" data-mp="21kjeh1oh7t7h">
                    No two coaching programs look alike. We start with a
                    conversation about your curriculum, your frameworks, and the
                    way your coaches work, then shape Nisa around what we find.
                  </p>
                  <button type="button" onClick={openTalkToUs} className="mt-7 inline-flex min-h-12 items-center justify-center rounded-xl bg-primary-700 px-7 py-3 font-medium text-white shadow-sm transition-colors hover:bg-primary-800 focus-visible:ring-primary-600" data-id="element-556" data-mp="2bnexfys65fa9">
                    Request a demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-557" data-mp="1srf0e9kc3ljt" />
    </div>;
};